import { createFileRoute } from "@tanstack/react-router";
import * as React from "react";
import { motion } from "framer-motion";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { vi } from "date-fns/locale";
import { Check, X, FileText, Clock, Save, Users, Search } from "lucide-react";
import { PortalLayout } from "@/components/portal-layout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth, useCanMutate } from "@/lib/auth";

export const Route = createFileRoute("/teacher/attendance")({ component: AttendancePage });

type Status = "present" | "absent" | "excused" | "late";

const STATUS_CFG: Record<Status, { label: string; bg: string; text: string; icon: React.ReactNode }> = {
  present: { label: "Có mặt", bg: "bg-emerald-500", text: "text-white", icon: <Check className="size-3.5" /> },
  absent: { label: "Vắng KP", bg: "bg-rose-500", text: "text-white", icon: <X className="size-3.5" /> },
  excused: { label: "Có phép", bg: "bg-amber-400", text: "text-amber-950", icon: <FileText className="size-3.5" /> },
  late: { label: "Muộn", bg: "bg-orange-400", text: "text-orange-950", icon: <Clock className="size-3.5" /> },
};

function AttendancePage() {
  const { user } = useAuth();
  const canMutate = useCanMutate();
  const qc = useQueryClient();
  const [classId, setClassId] = React.useState<string>("");
  const [sessionId, setSessionId] = React.useState<string>("");
  const [q, setQ] = React.useState("");
  const [statuses, setStatuses] = React.useState<Record<string, Status>>({});
  const [notes, setNotes] = React.useState<Record<string, string>>({});
  const [sessionNote, setSessionNote] = React.useState("");
  const [topic, setTopic] = React.useState("");

  const classesQ = useQuery({
    queryKey: ["teacher-classes", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase.from("classes").select("id, name").eq("teacher_id", user!.id).order("name");
      if (error) throw error;
      return data ?? [];
    },
  });

  React.useEffect(() => {
    if (!classId && classesQ.data?.[0]) setClassId(classesQ.data[0].id);
  }, [classesQ.data, classId]);

  const sessionsQ = useQuery({
    queryKey: ["sessions", classId],
    enabled: !!classId,
    queryFn: async () => {
      const { data: sessions } = await supabase
        .from("class_sessions").select("id, starts_at, room")
        .eq("class_id", classId).order("starts_at").limit(200);

      const sessionIds = (sessions ?? []).map(s => s.id);
      const { data: attCounts } = sessionIds.length
        ? await supabase.from("attendances").select("session_id").in("session_id", sessionIds)
        : { data: [] as { session_id: string }[] };
      const attendedSet = new Set((attCounts ?? []).map(a => a.session_id));

      return (sessions ?? []).map((s, i) => ({
        ...s,
        sessionNumber: i + 1,
        isAttended: attendedSet.has(s.id),
      }));
    },
  });

  React.useEffect(() => {
    if (!sessionsQ.data?.length) return;
    if (sessionId && sessionsQ.data.some(s => s.id === sessionId)) return;
    const now = new Date();
    const unattended = sessionsQ.data.filter(s => !s.isAttended && new Date(s.starts_at) <= now);
    if (unattended.length) {
      setSessionId(unattended[unattended.length - 1].id);
    } else {
      setSessionId(sessionsQ.data[sessionsQ.data.length - 1].id);
    }
  }, [sessionsQ.data, sessionId]);

  const studentsQ = useQuery({
    queryKey: ["class-students", classId],
    enabled: !!classId,
    queryFn: async () => {
      const { data: rows, error } = await supabase.from("enrollments").select("student_id").eq("class_id", classId).eq("is_active", true);
      if (error) throw error;
      const ids = (rows ?? []).map(r => r.student_id);
      if (ids.length === 0) return [];
      const { data: profs } = await supabase.from("profiles").select("id, display_name, email").in("id", ids);
      return (profs ?? []).sort((a, b) => (a.display_name ?? a.email).localeCompare(b.display_name ?? b.email));
    },
  });

  const existingQ = useQuery({
    queryKey: ["attendance", sessionId],
    enabled: !!sessionId,
    queryFn: async () => {
      const { data, error } = await supabase.from("attendances").select("student_id, status, note").eq("session_id", sessionId);
      if (error) throw error;
      return data ?? [];
    },
  });

  React.useEffect(() => {
    if (existingQ.data) {
      const sMap: Record<string, Status> = {};
      const nMap: Record<string, string> = {};
      existingQ.data.forEach(a => {
        sMap[a.student_id] = a.status as Status;
        if (a.note) nMap[a.student_id] = a.note;
      });
      setStatuses(sMap);
      setNotes(nMap);
    } else {
      setStatuses({});
      setNotes({});
    }
  }, [existingQ.data]);

  React.useEffect(() => {
    if (!sessionId) { setSessionNote(""); setTopic(""); return; }
    supabase.from("class_sessions").select("session_note, topic").eq("id", sessionId).maybeSingle()
      .then(({ data }) => {
        setSessionNote(data?.session_note ?? "");
        setTopic((data as { topic?: string } | null)?.topic ?? "");
      });
  }, [sessionId]);

  const saveMut = useMutation({
    mutationFn: async () => {
      const rows = Object.entries(statuses).map(([student_id, status]) => ({
        session_id: sessionId,
        student_id,
        status,
        marked_by: user?.id ?? null,
        note: notes[student_id]?.trim() || null,
      }));
      if (rows.length === 0) throw new Error("Chưa có học sinh nào được đánh dấu");
      const { error } = await supabase.from("attendances").upsert(rows, { onConflict: "session_id,student_id" });
      if (error) throw error;
      const { error: nErr } = await supabase.from("class_sessions")
        .update({ session_note: sessionNote.trim() || null }).eq("id", sessionId);
      if (nErr) throw nErr;
    },
    onSuccess: () => {
      toast.success("Đã lưu điểm danh + ghi chú");
      qc.invalidateQueries({ queryKey: ["attendance", sessionId] });
      qc.invalidateQueries({ queryKey: ["sessions", classId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const present = Object.values(statuses).filter(s => s === "present").length;
  const setOne = (id: string, s: Status) => setStatuses(p => ({ ...p, [id]: s }));
  const markAllPresent = () => {
    const m: Record<string, Status> = {};
    (studentsQ.data ?? []).forEach(s => { m[s.id] = "present"; });
    setStatuses(m);
  };

  return (
    <PortalLayout portal="teacher" title="Điểm danh" breadcrumb={[{ label: "Giáo viên", to: "/teacher" }, { label: "Điểm danh" }]}>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-card border rounded-2xl overflow-hidden shadow-sm">
        <div className="px-6 py-4 border-b flex flex-wrap items-center gap-3" style={{ background: "var(--portal-soft)" }}>
          <Select value={classId} onValueChange={(v) => { setClassId(v); setSessionId(""); }}>
            <SelectTrigger className="w-[200px] bg-white"><SelectValue placeholder="Chọn lớp" /></SelectTrigger>
            <SelectContent>
              {(classesQ.data ?? []).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sessionId} onValueChange={setSessionId}>
            <SelectTrigger className="w-[180px] bg-white"><SelectValue placeholder="Chọn buổi học" /></SelectTrigger>
            <SelectContent>
              {(sessionsQ.data ?? []).slice().reverse().map(s => (
                <SelectItem key={s.id} value={s.id}>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Buổi {s.sessionNumber}</span>
                    {s.isAttended && <span className="text-emerald-600">✓</span>}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1.5 px-3 py-1 bg-white rounded-full text-sm font-medium">
            <Users className="size-3.5" style={{ color: "var(--portal)" }} />
            {present}/{studentsQ.data?.length ?? 0} có mặt
          </div>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" onClick={markAllPresent} disabled={!canMutate}>Tất cả có mặt</Button>
            <Button size="sm" onClick={() => saveMut.mutate()} disabled={!canMutate || !sessionId || saveMut.isPending} className="gap-1.5" style={{ background: "var(--portal)", color: "white" }}>
              <Save className="size-4" />{saveMut.isPending ? "Đang lưu..." : "Lưu"}
            </Button>
          </div>
        </div>

        {sessionId && (
          <div className="px-6 py-3 border-b flex items-center gap-2">
            
            <Input
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              onBlur={async () => {
                await supabase.from("class_sessions")
                  .update({ topic: topic.trim() || null } as never)
                  .eq("id", sessionId);
              }}
              placeholder="Hôm nay học gì? (VD: Chương 5 — PT bậc 2)"
              disabled={!canMutate}
              className="h-8 text-sm flex-1"
            />
          </div>
        )}

        <div className="px-6 py-3 border-b">
          <div className="relative max-w-md">
            <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Tìm học sinh..." className="pl-9" />
          </div>
        </div>

        {(studentsQ.data?.length ?? 0) === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground">Lớp chưa có học sinh ghi danh.</div>
        ) : (
          <ul className="divide-y">
            {(studentsQ.data ?? []).filter(s => (s.display_name ?? s.email).toLowerCase().includes(q.toLowerCase())).map((s, i) => {
              const status = statuses[s.id];
              return (
                <li key={s.id} className="flex items-center gap-4 px-6 py-3">
                  <div className="size-8 rounded-full grid place-items-center text-xs font-medium bg-muted">{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{s.display_name ?? s.email}</div>
                    {status && <div className="text-[11px] text-muted-foreground mt-0.5">{STATUS_CFG[status].label}</div>}
                  </div>
                  <div className="flex gap-1.5">
                    {(["present", "absent", "excused", "late"] as const).map(st => {
                      const cf = STATUS_CFG[st];
                      const active = status === st;
                      return (
                        <button key={st} onClick={() => canMutate && setOne(s.id, st)} disabled={!canMutate}
                          className={cn("size-8 rounded-lg grid place-items-center transition-all disabled:opacity-50",
                            active ? `${cf.bg} ${cf.text} shadow-md` : "bg-muted text-muted-foreground hover:bg-muted/70")}
                          title={cf.label}>
                          {cf.icon}
                        </button>
                      );
                    })}
                  </div>
                  <Input
                    value={notes[s.id] ?? ""}
                    onChange={(e) => setNotes(prev => ({ ...prev, [s.id]: e.target.value }))}
                    placeholder="Ghi chú..."
                    disabled={!canMutate}
                    className="w-36 h-7 text-xs"
                  />
                </li>
              );
            })}
          </ul>
        )}

        {sessionId && (
          <div className="px-6 py-4 border-t bg-muted/20">
            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Ghi chú cho buổi học</label>
            <Textarea
              value={sessionNote}
              onChange={(e) => setSessionNote(e.target.value)}
              placeholder="VD: Dạy bù chương 5, có 2 HS vào muộn..."
              rows={2}
              disabled={!canMutate}
              className="text-sm bg-white"
            />
          </div>
        )}
      </motion.div>
    </PortalLayout>
  );
}
